import { Action } from "redux";

import { ConditionParams } from "@multiweb/clients/dataReader";
import { IPublicAttributeInfo } from "@multiweb/types/contracts";
import { ExtendedPropertiesType, IFormContext, IOrderBy, IWorkflowMenuItem } from "@multiweb/types/multiweb";
import { FetchingState, ReactAction, ReactActionId, RecordType, RootNodeTypedObject, TypedObject } from "@multiweb/types/react";
import {
    CHECK_ALL_RECORDS,
    DELETE_DATASOURCE,
    DESELECT_ALL_RECORDS,
    EXECUTE_WORKFLOW_ACTION,
    GET_WORKFLOW_ACTIONS_ERROR,
    GET_WORKFLOW_ACTIONS_REQUEST,
    GET_WORKFLOW_ACTIONS_SUCCESS,
    INIT_DATASOURCE,
    LOAD_ATTRIBUTE_INFO,
    LOAD_ATTRIBUTE_INFO_ERROR,
    LOAD_ATTRIBUTE_INFO_SUCCESS,
    LOAD_DATA,
    LOAD_DATA_ERROR,
    LOAD_DATA_SUCCESS,
    REFRESH_DATA,
    REFRESH_DATA_ERROR,
    REFRESH_DATA_SUCCESS,
    SELECT_NEXT_RECORD,
    SELECT_PREVIOUS_RECORD,
    SELECT_RECORD,
    SELECT_RECORDS,
    SET_ATTRIBUTES,
    SET_DATASOURCE_MULTISELECT_MODE,
    SET_DISABLE_LOADING,
    SET_HAS_DETAIL_FORM,
    SET_LAST_SELECTED_RECORD_ID,
    SET_ORDER_BY,
    SET_ROOT_NODES,
    TOGGLE_CHECK_ALL_RECORDS,
    TOGGLE_CHECK_ON_SELECTED_RECORDS,
    TOGGLE_ORDER_BY,
    TOGGLE_SELECTED_RECORD_CHECK,
    UNCHECK_ALL_RECORDS,
    UPDATE_FULLTEXT_CONDITION,
    UPDATE_MASTER_FRAME_CONDITION,
    UPDATE_RECORD_ACTIONS,
    UPDATE_RECORD_ACTIONS_SUCCESS,
} from "./constants";

/**
 * Definice stavu pro mapu DataSource.
 */
export type DataSourceMapState = TypedObject<IDataSourceState>;

/**
 * Typ identifikátoru pro DataSource.
 */
export type RecordIdKeyType = string;

/**
 * Definice stavu pro instanci DataSource.
 */
export interface IDataSourceState {
    /** Konfigurace DataSource. */
    readonly config: Readonly<IDataSourceConfig>;
    /** Attributy, podle kterých se řadí data z aplikačního serveru. */
    readonly orderBy: ReadonlyArray<IOrderBy>;
    /** Podmínky pro načítání dat. */
    readonly conditions: IDataSourceConditions;
    /** Veřejné informace k atributům. */
    readonly attributeInfo: TypedObject<IPublicAttributeInfo>;
    /** Načtená data. */
    readonly data: IDataSourceData;
    /** Informace související s režimem výběru více záznamů. */
    readonly multiselect: IDataSourceMultiselect;
    /** Informace o načítání dat. */
    readonly loading: IDataSourceLoading;
    /** Informace o akcích. */
    readonly actions: IDataSourceActions;
    /** Informace o workflow akcích */
    readonly workflowActions: IWorkflowActions;
}

/**
 * Základní informace o atributu.
 */
export interface IDataSourceAttribute {
    /** Název atributu. */
    attributeName: string;
    /** Určuje, zda se atribut vykresluje jako ikona. */
    isIcon: boolean | null;
    /** Udává název ikony, která má vykreslena spolu s atributem. Vykreslí pevnou ikonu spolu s hodnotou. */
    iconName: string | null;
    /** Lokalizační klíč pro použití v hlavičce. */
    locKey: string | null;
    /** Formát, v jakém má být hodnota zobrazena. */
    displayFormat: string | null;
    /** Určuje, jestli má být atribut skryt. Hodnoty pro atribut se normálně načítají, atribut se pouze nezobrazuje. */
    hidden: boolean | null;
    /** Název atributu, podle kterého má dojít k obarvení. */
    backgroundAttrName: string | null;
    /** Určuje, jaký renderer atribut využívá. */
    attributeRendererKey: string | null;
    /** Příznak, jestli se májí ořezávat načítaná data atributu. Výchozí true */
    trimValue?: boolean;
    /** Dodatečné vlastnosti atributu */
    extendedProperties: ExtendedPropertiesType;
}

export class IDataSourceAttribute {
    private static _properties: (keyof IDataSourceAttribute)[] = ["attributeName", "isIcon", "locKey", "displayFormat", "hidden", "backgroundAttrName", "attributeRendererKey"];
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            this._properties.every((value) => Reflect.has(instance, value));
    }
}

export interface IFulltextSearchCondition {
    /** Podmínka pro načítání dat získaná z fulltextového vyhledávání. */
    readonly condition?: string;
    /** Vyhledávaný text ve fulltextovém vyhledávání */
    readonly searchString?: string;
}

/**
 * Podmínky DataSource.
 */
export interface IDataSourceConditions {
    /** Podmínka pro načítání dat. */
    readonly condition: string;
    /** Text fulltextového vyhledávání a podmínka */
    readonly fulltextCondition: IFulltextSearchCondition;
    /** Podmínka pro načítání dat získaná z masterFrame. */
    readonly masterFrameCondition: string;
    /** Argumenty podmínky. */
    readonly conditionParams: ConditionParams;
}

/**
 * Informace o načítání dat.
 */
export interface IDataSourceLoading {
    /** Stav načítání. */
    readonly fetchingState: FetchingState;
    /** Index od kterého začíná další načítání. */
    readonly startIndex: number;
    /** Počet dat, která se načítají. */
    readonly fetchingCount: number;
    /** Určuje, zda jsou k dočtení další data. */
    readonly hasNextItems: boolean;
    /** Určuje, zda je načítání záznamů zakázáno. Využito například v mixinu @see hasMasterFrame. */
    readonly disabledLoading: boolean;
}

/**
 * Informace o workflow akcích.
 */
export interface IWorkflowActions {
    /** Stav načítání. */
    readonly fetchingState: FetchingState;
    /** Workflow akce pro daný záznam. */
    readonly actions: TypedObject<IWorkflowMenuItem>;
    /** Řadící parametr workflow akcí. */
    readonly actionsOrder: ReadonlyArray<string>;
}

/**
 * Informace o akcích.
 */
export interface IDataSourceActions {
    /** Načtené akce. */
    readonly actions: TypedObject<ReactAction>;
    /** Identifikátory akcí, které jdou spustit nad každým záznamem. */
    readonly recordActionsIds: ReadonlyArray<ReactActionId>;
    /** Určuje, zda mohu otevřít detail záznamu. */
    readonly hasDetailForm: boolean;
}

/**
 * Informace související s režimem výběru více záznamů.
 */
export interface IDataSourceMultiselect {
    /** Příznaky určující, zda je daný záznam vybrán. */
    readonly selectedRecords: TypedObject<boolean>;
    /** Objekt určující, zda je daný záznam označen. */
    readonly checkedRecords: TypedObject<boolean>;
    /** Identifikátor naposledy označeného záznamu. */
    readonly lastSelectedRecordId: string;
    /** Určuje, zda je aktivní multiselect mód. */
    readonly multiselectMode: boolean;
    /** Určuje, zdali jsou vybrány všechny záznamy (i ty ještě nezobrazené). */
    readonly isCheckedAll: boolean;
    /** Zda má i nově příchozí zaškrtávat. */
    readonly keepCheckedIncomingData: boolean;
    /** Kořenové uzly a informace o nich. */
    readonly rootNodes: RootNodeTypedObject;
}

/**
 * Načtená data.
 */
export interface IDataSourceData {
    /** Načtená data z MultiWebu. */
    readonly records: TypedObject<RecordType>;
    /** Seřazené identifikátory načtených dat. */
    readonly recordsIds: ReadonlyArray<string>;
}

/**
 * Error, který DataSource vytvoří, pokud chce načíst data, ale dataSource má vypnuté načítání dat.
 */
export class DisabledLoadingError extends Error {
    /**
     * Vytvoří nový Error.
     * @param dataSourceId Identifikátor DataSource.
     */
    constructor(dataSourceId: string) {
        super(`Loading data for DataSource (id=${dataSourceId}) is disabled.`);
    }
}

/**
 * Typ klíče.
 */
type CustomKey = string | number;

/**
 * Klíč je název metody ze sagy a data.
 */
export type CustomMethodDataType<K extends CustomKey, V> = {
    [key in K]: { data: V[] };
};

export interface IDataSourceConfigBase {
    /** Jméno objektu, který DataSource načítá z databáze. */
    readonly objectName: string;
    /** Klíč k identifikátoru uvnitř záznamu. */
    readonly recordIdKey: RecordIdKeyType;
    /** Kontext formuláře */
    readonly formContext: Readonly<IFormContext>;
    /** Zda je data source perzistentní */
    readonly isPersistent: boolean;
    /** Identifikátor vlastního DataSource */
    readonly customDataSourceId?: string;
    /** Vlastní data předaná DataSource */
    readonly customMethodsData?: CustomMethodDataType<CustomKey, unknown>;
}

/**
 * Konfigurace DataSource ze store pro načítání dat.
 */
export interface IDataSourceConfig extends IDataSourceConfigBase {
    /** Atributy, které DataSource načítá z databáze. */
    readonly attributes: TypedObject<IDataSourceAttribute>;
    /** Seřazené názvy dataAtributů podle definice formuláře. */
    readonly attributeNames: ReadonlyArray<string>;
    /** Seznam attributů, jejichž hodnota se nemá při načítání ořezávat */
    readonly fullLengthAttributes: ReadonlyArray<string>;
    /** Klíč k identifikátoru uvnitř záznamu. */
    readonly recordIdKey: RecordIdKeyType;
    /** Kontext formuláře */
    readonly formContext: Readonly<IFormContext>;
    /** Příznak, jestli má Workflow */
    readonly hasWorkflow: boolean;
}

/**
 * Konfigurace DataSource určená pro inicializaci.
 * Zde jsou atributy v poli a reducer si je uloží normalizovaně
 * (tj. **object attributes**, kde jsou atributy pod jejich jménem a **pole attributeNames**,
 * kde jsou jména atributů v pořadí z inicializace).
 */
export interface IDataSourceInitConfig extends IDataSourceConfigBase {
    /** Atributy, které DataSource načítá z databáze. */
    readonly attributes: ReadonlyArray<IDataSourceAttribute>;
    /** Klíč k identifikátoru uvnitř záznamu. */
    readonly recordIdKey: RecordIdKeyType;
    /** Kontext formuláře */
    readonly formContext: Readonly<IFormContext>;
    /** Příznak, jestli má Workflow */
    readonly hasWorkflow: boolean;
}

/**
 * Výčet sousedních elementů.
 */
export enum ElementSiblings {
    nextElementSibling = "nextElementSibling",
    previousElementSibling = "previousElementSibling",
}

/**
 * Parametry pro selektor k získání konkrétního atributu.
 */
export type AttributeSelectorParams = {
    readonly dataSourceKey: string;
    readonly attributeName: string;
};

/**
 * Parametry pro selektory k jednotlivým akcím.
 */
export type ActionSelectorParams = {
    readonly dataSourceKey: string;
    readonly actionId: string;
};

/**
 * Parametry pro selektory k jednotlivým záznamům.
 */
export type RecordSelectorParams = {
    readonly dataSourceKey: string;
    readonly recordId: string;
};

/**
 * Parametry pro selektor k jednotlivým workflow akcím.
 */
export type WorkflowActionSelectorParams = {
    readonly dataSourceKey: string;
    readonly workflowActionId: string;
};

export type RecordAttributeSelectorParams = AttributeSelectorParams & RecordSelectorParams;

/**
 * Pomocná akce, kterou musí rozšiřovat akce, které jsou mapovány
 * na jednotlivé instance `DataSource`.
 */
export interface IDataSourceMappedAction extends Action<string> {
    readonly meta: {
        readonly dataSourceKey: string;
    };
}

export class IDataSourceMappedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance?.meta?.dataSourceKey;
    }
}

/**
 * Akce pro nastavení DataSource do inicializovaného stavu.
 */
export interface IInitDataSourceAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly config: IDataSourceInitConfig;
        readonly hasDetailForm: boolean;
        readonly orderBy: ReadonlyArray<IOrderBy>;
        readonly conditions: Partial<IDataSourceConditions>;
        readonly attributeInfo: ReadonlyArray<IPublicAttributeInfo>;
    };
}

export class IInitDataSourceAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === INIT_DATASOURCE;
    }
}

/**
 * Akce pro smazání DataSource.
 */
export interface IDeleteDataSourceAction extends IDataSourceMappedAction { }

export class IDeleteDataSourceAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === DELETE_DATASOURCE;
    }
}

/**
 * Akce pro zavolání načtení informací k atributům.
 */
export interface ILoadAttributeInfoAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly attributesNames: ReadonlyArray<string>;
    };
}
export class ILoadAttributeInfoAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === LOAD_ATTRIBUTE_INFO;
    }
}

/**
 * Akce pro uložení úspěšně načtených informací k atributům.
 */
export interface ILoadAttributeInfoSuccessAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly attributeInfo: ReadonlyArray<IPublicAttributeInfo>;
    };
}

export class ILoadAttributeInfoSuccessAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === LOAD_ATTRIBUTE_INFO_SUCCESS;
    }
}

/**
 * Akce pro uložení neúspěšně načtených informací k atributům.
 */
export interface ILoadAttributeInfoErrorAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly message?: string;
    };
}

export class ILoadAttributeInfoErrorAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === LOAD_ATTRIBUTE_INFO_ERROR;
    }
}

/**
 * Akce pro načtení nových dat.
 */
export interface ILoadDataAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly fetchCount: number;
    };
}

export class ILoadDataAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === LOAD_DATA;
    }
}

/**
 * Akce pro potvrzení úspěšného načtení dat.
 */
export interface ILoadDataSuccessAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly data: ReadonlyArray<RecordType>;
    };
}

export class ILoadDataSuccessAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === LOAD_DATA_SUCCESS;
    }
}

/**
 * Akce pro oznámení chybného načtení dat.
 */
export interface ILoadDataErrorAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly message: string | undefined;
    };
}

export class ILoadDataErrorAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === LOAD_DATA_ERROR;
    }
}

/**
 * Akce pro aktualizaci dat
 */

export interface IRefreshDataAction extends IDataSourceMappedAction { }

export class IRefreshDataAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === REFRESH_DATA;
    }
}

/**
 * Akce pro potvrzení úspěšné aktualizace dat.
 */
export interface IRefreshDataSuccessAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly data: ReadonlyArray<RecordType>;
    };
}

export class IRefreshDataSuccessAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === REFRESH_DATA_SUCCESS;
    }
}

/**
 * Akce pro oznámení chybné aktualizace dat.
 */
export interface IRefreshDataErrorAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly message?: string;
    };
}

export class IRefreshDataErrorAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === REFRESH_DATA_ERROR;
    }
}

/**
 * Akce pro aktualizaci fulltextové podmínky DataSource.
 */
export interface IUpdateFulltextDataSourceCondition extends IDataSourceMappedAction {
    readonly payload: {
        readonly newCondition: IFulltextSearchCondition;
        readonly refreshOnChange: boolean;
    };
}

export class IUpdateFulltextDataSourceCondition {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === UPDATE_FULLTEXT_CONDITION;
    }
}

/**
 * Akce pro aktualizaci masterFrame podmínky DataSource.
 */
export interface IUpdateMasterFrameDataSourceCondition extends IDataSourceMappedAction {
    readonly payload: {
        readonly newCondition: string;
        readonly refreshOnChange: boolean;
        readonly conditionParams?: ConditionParams;
        readonly isMasterRecordSelected?: boolean;
    };
}

export class IUpdateMasterFrameDataSourceCondition {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === UPDATE_MASTER_FRAME_CONDITION;
    }
}

/**
 * Akce pro aktualizaci dostupných akcí pro záznam z MW.
 */
export interface IUpdateRecordActionsAction extends IDataSourceMappedAction { }

export class IUpdateRecordActionsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === UPDATE_RECORD_ACTIONS;
    }
}

/**
 * Akce pro úspěšnou aktualizaci dostupných akcí pro záznam z MW.
 */
export interface IUpdateRecordActionsSuccessAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly actions: ReadonlyArray<ReactAction>;
    };
}

export class IUpdateRecordActionsSuccessAction extends IDataSourceMappedAction {
    public static override[Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === UPDATE_RECORD_ACTIONS_SUCCESS;
    }
}

/**
 * Akce pro nastavení řazení na DataSource.
 *
 * příklad orderBy: "attr1;DESC attr3"
 */
export interface ISetOrderByAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly orderBy: ReadonlyArray<IOrderBy>;
    };
}

export class ISetOrderByAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === SET_ORDER_BY;
    }
}

/**
 * Akce pro přepnutí řazení na DataSource.
 */
export interface IToggleOrderByAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly attributeName: string;
        readonly clearOthers: boolean;
    };
}

export class IToggleOrderByAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === TOGGLE_ORDER_BY;
    }
}

/**
 * Akce pro zapnutí/vypnutí režimu výběru více záznamů.
 */
export interface ISetDatasourceMultiselectModeAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly multiselectMode: boolean;
    };
}

export class ISetDatasourceMultiselectModeAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === SET_DATASOURCE_MULTISELECT_MODE;
    }
}

/**
 * Akce, která nastavuje atributy dataSource.
 */
export interface ISetAttributesAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly attributes: ReadonlyArray<IDataSourceAttribute>;
    };
}

export class ISetAttributesAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === SET_ATTRIBUTES;
    }
}

/**
 * Akce pro přepnutí označení(isChecked) na vybraných(isSelected) záznamech v DataSource.
 */
export interface IToggleCheckOnSelectedRecordsAction extends IDataSourceMappedAction { }

export class IToggleCheckOnSelectedRecordsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === TOGGLE_CHECK_ON_SELECTED_RECORDS;
    }
}

/**
 * Akce pro označení / odznačení konkrétního záznamu v DataSource.
 */
export interface IToggleSelectedRecordCheckAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly recordId: string;
        readonly ownerId?: string;
    };
}

export class IToggleSelectedRecordCheckAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === TOGGLE_SELECTED_RECORD_CHECK;
    }
}

/**
 * Akce pro výběr všech načtených a přednačtených záznamu v DataSource.
 */
export interface ICheckAllRecordsAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly keepIncomingDataChecked: boolean;
    };
}

export class ICheckAllRecordsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === CHECK_ALL_RECORDS;
    }
}

/**
 * Akce pro zaškrtnutí či odškrtnutí všech záznamů z Datasource
 */
export interface IToggleCheckAllRecordsAction extends IDataSourceMappedAction { }

export class IToggleCheckAllRecordsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === TOGGLE_CHECK_ALL_RECORDS;
    }
}

/**
 * Akce pro vyběr dalšího záznamu.
 */
export interface ISelectNextRecord extends IDataSourceMappedAction {
    readonly payload: {
        readonly clearOtherSelections: boolean;
    };
}

export class ISelectNextRecord {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === SELECT_NEXT_RECORD;
    }
}

/**
 * Akce pro vyběr předchozího záznamu.
 */
export interface ISelectPreviousRecord extends IDataSourceMappedAction {
    readonly payload: {
        readonly clearOtherSelections: boolean;
    };
}

export class ISelectPreviousRecord {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === SELECT_PREVIOUS_RECORD;
    }
}

/**
 * Akce, která nastavuje informaci o tom, zda je na DataSource zakázáno načítání dat.
 */
export interface ISetDisableLoadingAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly value: boolean;
    };
}

export class ISetDisableLoadingAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === SET_DISABLE_LOADING;
    }
}

/**
 * Akce, která nastavuje informaci o tom, zda se na DataSource může otevřít formulář zobrazující detail záznamu.
 */
export interface ISetHasDetailFormAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly hasDetailForm: boolean;
    };
}

export class ISetHasDetailFormAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === SET_HAS_DETAIL_FORM;
    }
}

/**
 * Akce pro zrušení výberů všech záznamů.
 */
export interface IDeselectAllRecordsAction extends IDataSourceMappedAction { }

export class IDeselectAllRecordsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === DESELECT_ALL_RECORDS;
    }
}

/**
 * Akce pro výběr záznamů daných identifikátory.
 */
export interface ISelectRecordsAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly selectedRecordsIds: ReadonlyArray<string>;
    };
}

export class ISelectRecordsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === SELECT_RECORDS;
    }
}

/**
 * Akce pro výběr jednoho záznamu.
 */
export interface ISelectRecordAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly recordId: string;
    };
}

export class ISelectRecordAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === SELECT_RECORD;
    }
}

/**
 * Akce pro odznačení všech záznamů.
 */
export interface IUncheckAllRecordsAction extends IDataSourceMappedAction { }

export class IUncheckAllRecordsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === UNCHECK_ALL_RECORDS;
    }
}

/**
 * Akce pro nastavení identifikátoru naposledy označeného záznamu.
 */
export interface ISetLastSelectedRecordIdAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly recordId: string;
    };
}

export class ISetLastSelectedRecordIdAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === SET_LAST_SELECTED_RECORD_ID;
    }
}

/**
 * Akce pro získání workflow akcí
 */
export interface ILoadWorkflowActionsAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly recordId: string;
    };
}

export class ILoadWorkflowActionsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === GET_WORKFLOW_ACTIONS_REQUEST;
    }
}

/**
 * Akce pro uložení workflow akcí do storu
 */
export interface ILoadWorkflowActionsSuccessAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly workflowActions: ReadonlyArray<IWorkflowMenuItem>;
    };
}

export class ILoadWorkflowActionsSuccessAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === GET_WORKFLOW_ACTIONS_SUCCESS;
    }
}

/**
 * Akce pro chybné získání workflow akcí ze serveru
 */
export interface ILoadWorkflowActionsErrorAction extends IDataSourceMappedAction {
    readonly payload: {
        readonly message: string;
    };
}

export class ILoadWorkflowActionsErrorAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === GET_WORKFLOW_ACTIONS_ERROR;
    }
}

export interface IExecuteWorkflowAction extends IDataSourceMappedAction {
    readonly payload: {
        workflowActionId: string;
        recordId: string;
    };
}

export class IExecuteWorkflowAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === EXECUTE_WORKFLOW_ACTION;
    }
}

/**
 * Akce pro nastavení uzlových záznamů.
 */
export interface ISetRootNodes extends IDataSourceMappedAction {
    readonly payload: {
        readonly nodes: RootNodeTypedObject;
    };
}

export class ISetRootNodes {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IDataSourceMappedAction && instance.type === SET_ROOT_NODES;
    }
}
